import React, { createContext, useContext, useEffect, useMemo, useState } from 'react';
import type { UserManager } from 'oidc-client-ts';
import { AuthProvider as OidcProvider, useAuth as useOidc } from 'react-oidc-context';
import { upsertSelf } from '../lib/callables.js';
import { signInRedirect, signOutRedirect } from './oidc.js';

interface UserRecord {
  allowed: boolean;
  isAdmin: boolean;
}

/** The signed-in user, from the Cognito ID token. */
export interface AppUser {
  /** Lower-cased email — the same uid the backend stamps on createdBy.uid. */
  uid: string;
  email: string | null;
  displayName: string | null;
}

interface AuthContextValue {
  user: AppUser | null;
  userRecord: UserRecord | null;
  loading: boolean;
  /** Sign-in failure reported by Cognito/Entra on the redirect back. */
  error: string | null;
  signIn: () => Promise<void>;
  signOut: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | null>(null);

function AuthBridge({
  children,
  initialError,
}: {
  children: React.ReactNode;
  initialError: string | null;
}) {
  const oidc = useOidc();
  const [userRecord, setUserRecord] = useState<UserRecord | null>(null);

  const profile = oidc.user && !oidc.user.expired ? oidc.user.profile : null;
  const email = typeof profile?.email === 'string' ? profile.email.toLowerCase() : null;
  const name =
    profile?.name ??
    ([profile?.given_name, profile?.family_name].filter(Boolean).join(' ') || null);
  const uid = email ?? profile?.sub ?? null;

  const user = useMemo<AppUser | null>(
    () => (uid ? { uid, email, displayName: name } : null),
    [uid, email, name],
  );

  // Keyed on the identity, not the token, so silent renewals don't re-run it.
  useEffect(() => {
    if (!uid) {
      setUserRecord(null);
      return;
    }
    let cancelled = false;
    setUserRecord(null);
    upsertSelf()
      .then((result) => !cancelled && setUserRecord(result.data))
      .catch(() => !cancelled && setUserRecord({ allowed: false, isAdmin: false }));
    return () => {
      cancelled = true;
    };
  }, [uid]);

  const loading = oidc.isLoading || (user !== null && userRecord === null);

  async function signOut() {
    setUserRecord(null);
    await signOutRedirect();
  }

  return (
    <AuthContext.Provider
      value={{
        user,
        userRecord,
        loading,
        error: initialError ?? oidc.error?.message ?? null,
        signIn: signInRedirect,
        signOut,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function AuthProvider({
  userManager,
  initialError = null,
  children,
}: {
  userManager: UserManager;
  initialError?: string | null;
  children: React.ReactNode;
}) {
  return (
    // main.tsx completes the redirect callback before mounting, so the
    // provider only has to load the stored user.
    <OidcProvider userManager={userManager} skipSigninCallback>
      <AuthBridge initialError={initialError}>{children}</AuthBridge>
    </OidcProvider>
  );
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used inside AuthProvider');
  return ctx;
}
